import type Tool from '../../tools/Tool';
import { type ToolArgsWithController } from '../../tools/Tool';
import WithHint from '../WithHint';
import toolbarStyles from './Toolbar.module.css';
import tools from '../../tools';
import React from 'react';
import classnames from 'classnames';

interface ToolButtonProps extends ToolArgsWithController {
  tool: typeof Tool;
  className?: string; // Optional class name to add to the button.
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

export const ToolButtonComponent = ({
  tool,
  placement,
  className,
  onMouseDown: externalOnMouseDown,
  ...toolArgs
}: ToolButtonProps) => {
  const enabled = tool.isEnabled(toolArgs);
  const active = tool.isActive(toolArgs);
  const toolButtonIcon = tool.renderToolbarButtonIcon(toolArgs);

  const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (externalOnMouseDown) {
      externalOnMouseDown(event);
    } else {
      if (enabled) {
        tools.fireToolAction(tool, event, toolArgs);
      } else {
        // Prevent blurring the editor.
        event.preventDefault();
      }
    }
  };

  const ariaAttributes: any = {
    'aria-label': tool.toolName,
  };
  if (!enabled) {
    ariaAttributes['aria-disabled'] = true;
  }
  if (active) {
    ariaAttributes['aria-selected'] = true;
  }

  return (
    <div
      className={classnames(className, toolbarStyles.toolbarButton)}
      role="button"
      data-testid={`tool-${tool.name}`}
      onMouseDown={onMouseDown}
      tabIndex="-1"
      {...ariaAttributes}
    >
      {toolButtonIcon}
    </div>
  );
};

const ToolButton = (props: ToolButtonProps) => {
  const {
    tool,
    placement,
    className,
    onMouseDown: externalOnMouseDown,
    ...toolArgs
  } = props;
  let button = tool.renderToolbarComponent(toolArgs);

  if (!button) {
    button = (
      <ToolButtonComponent
        tool={tool}
        placement={placement}
        className={className}
        onMouseDown={externalOnMouseDown}
        {...toolArgs}
      />
    );
  }

  return (
    <WithHint tool={tool} toolArgs={toolArgs}>
      {button}
    </WithHint>
  );
};

export default ToolButton;
